/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import {
  Camera,
  CameraResultType,
  CameraSource,
  Photo,
} from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Directory, Encoding, Filesystem } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';
import { Platform } from '@ionic/angular';
import { FILE_LIMIT } from '../../constants/files';
import {
  FILE_ERRORS,
  PHOTO_EXTENSTIONS,
  UserFile,
} from '../file/model/file.model';
import { UserPhoto } from './model/photo.model';

@Injectable({
  providedIn: 'root',
})
export class PhotoService {
  public photos: UserPhoto[] = [];
  private PHOTO_STORAGE = 'photos';
  private deviceInfo;

  constructor(private platform: Platform) {
    Device.getInfo().then(info => {
      this.deviceInfo = info;
    });
  }

  public async loadPhotos() {
    // Retrieve cached photo array data
    try {
      const photoList = await Preferences.get({ key: this.PHOTO_STORAGE });
      this.photos = JSON.parse(photoList.value) || [];
    } catch (ignore) {
      this.photos = [];
    }

    // If running on the web...
    if (!this.platform.is('hybrid')) {
      // Display the photo by reading into base64 format
      for (let photo of this.photos) {
        // Read each saved photo's data from the Filesystem
        try {
          const readFile = await Filesystem.readFile({
            path: photo.filepath,
            directory: Directory.Data,
          });

          // Web platform only: Load the photo as base64 data
          photo.webviewPath = `data:image/jpeg;base64,${readFile.data}`;
        } catch (ignore) {}
      }
    }
  }

  /* Use the device camera to take a photo:
  // https://capacitor.ionicframework.com/docs/apis/camera

  // Store the photo data into permanent file storage:
  // https://capacitor.ionicframework.com/docs/apis/filesystem

  // Store a reference to all photo filepaths using Preferences API:
  // https://capacitor.ionicframework.com/docs/apis/preferences
  */
  public async addPhoto(saveImage = true) {
    // Take a photo
    let capturedPhoto;
    try {
      if (this.platform.is('hybrid')) {
        // Avoiding a data issue seen on mobile where the data increases on selection instead of being left unchanged. This is by lowering the initial quality.
        capturedPhoto = await Camera.getPhoto({
          resultType: CameraResultType.Uri, // file-based data; provides best performance
          source: CameraSource.Prompt, // automatically take a new photo with the camera
          quality: 50, // highest quality (0 to 100)
        });
      } else {
        capturedPhoto = await Camera.getPhoto({
          resultType: CameraResultType.Uri, // file-based data; provides best performance
          source: CameraSource.Prompt, // automatically take a new photo with the camera
          quality: 100, // highest quality (0 to 100)
        });
      }
    } catch (error) {
      throw new Error(FILE_ERRORS.canceled);
    }
    // Convert to base 64 so we can see how big it is and the extension
    const base64Data = await this.readAsBase64(capturedPhoto);
    try {
      this.validatePhoto(base64Data);
    } catch (reThrow) {
      capturedPhoto = undefined;
      throw reThrow;
    }
    // If image is within size range and right type, save to to local storage
    var savedPhoto;
    if (base64Data.includes('base64,')) {
      savedPhoto = await this.savePhoto(
        base64Data.split('base64,')[1],
        capturedPhoto.webPath,
        capturedPhoto.format,
      );
    } else {
      savedPhoto = await this.savePhoto(
        base64Data,
        capturedPhoto.webPath,
        capturedPhoto.format,
      );
    }

    // Add new photo to photos array
    if (saveImage) this.photos.unshift(savedPhoto);

    // Cache all photo data for future retrieval
    if (saveImage)
      Preferences.set({
        key: this.PHOTO_STORAGE,
        value: JSON.stringify(this.photos),
      });
    return savedPhoto;
  }

  // Save picture to file on device
  private async savePhoto(base64Data: string, webPath: string, format: string) {
    // Write the file to the data directory
    // Need to make sure the data is B64 encoded before writting it, else the write may fail.
    // The Filesystem plugin works differntly based on platform, web need encdoing to avoid a bug, but we don't want to encode on mobile
    const fileName = new Date().getTime() + '.' + format;
    var savedPhoto;
    if (this.platform.is('hybrid')) {
      savedPhoto = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Data,
      });
    } else {
      savedPhoto = await Filesystem.writeFile({
        path: fileName,
        data: base64Data,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });
    }
    if (this.platform.is('hybrid')) {
      // Display the new image by rewriting the 'file://' path to HTTP
      // Details: https://ionicframework.com/docs/building/webview#file-protocol
      return {
        filepath: savedPhoto.uri,
        webviewPath: Capacitor.convertFileSrc(savedPhoto.uri),
        extension: format,
        base64: base64Data,
      } as UserPhoto;
    } else {
      // Use webPath to display the new image instead of base64 since it's
      // already loaded into memory
      return {
        filepath: fileName,
        webviewPath: webPath,
        extension: format,
        base64: base64Data,
      } as UserPhoto;
    }
  }

  // Read camera photo into base64 format based on the platform the app is running on
  public async readAsBase64(photo: Photo) {
    // "hybrid" will detect Cordova or Capacitor
    if (this.platform.is('hybrid')) {
      // Read the file into base64 format, convert blob to base64 if necessary
      const file = await Filesystem.readFile({
        path: photo.path,
      });
      if (typeof file.data === 'string') {
        return file.data as string;
      } else {
        return (await this.convertBlobToBase64(file.data as Blob)) as string;
      }
    } else {
      // Fetch the photo, read as a blob, then convert to base64 format
      const response = await fetch(photo.webPath!);
      const blob = await response.blob();

      return (await this.convertBlobToBase64(blob)) as string;
    }
  }

  // Delete picture by removing it from reference data and the filesystem
  public async deletePhoto(photo: UserPhoto) {
    // Remove this photo from the Photos reference data array
    this.photos.splice(this.photos.indexOf(photo), 1);
    // Update photos array cache by overwriting the existing photo array
    Preferences.set({
      key: this.PHOTO_STORAGE,
      value: JSON.stringify(this.photos),
    });
    // delete photo file from filesystem
    const filename = photo.filepath.substring(
      photo.filepath.lastIndexOf('/') + 1,
    );
    if (this.platform.is('hybrid')) {
      await Filesystem.deleteFile({
        path: photo.filepath,
      });
    } else {
      await Filesystem.deleteFile({
        path: filename,
        directory: Directory.Data,
      });
    }
  }

  // Delete pictures by removing it from reference data and the filesystem
  public async deletePhotos(photos: UserPhoto[]) {
    for (let photo of photos) {
      try {
        await this.deletePhoto(photo);
      } catch (ignore) {}
    }
  }

  public blobToBase64(blob: Blob): Promise<string | ArrayBuffer> {
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result!);
      reader.readAsDataURL(blob);
    });
  }

  public validatePhoto(base64Data: string) {
    if (this.deviceInfo.platform === 'web') {
      if (!this.isValidImageFile(base64Data)) {
        throw new Error(FILE_ERRORS.wrongMime.valueOf());
      }
    }

    if (
      this.calculateImageSize(
        base64Data.includes(',') ? base64Data.split(',')[1] : base64Data,
      ) > FILE_LIMIT
    ) {
      throw new Error(FILE_ERRORS.wrongSize.valueOf());
    }
  }

  public calculateImageSize(base64String) {
    let padding;
    let inBytes;
    let base64StringLength;
    if (base64String.endsWith('==')) {
      padding = 2;
    } else if (base64String.endsWith('=')) {
      padding = 1;
    } else {
      padding = 0;
    }
    base64StringLength = base64String.length;
    inBytes = (base64StringLength / 4) * 3 - padding;
    return inBytes;
  }

  private isValidImageFile(base64Data: string) {
    let imagePath = base64Data.split(';');
    return PHOTO_EXTENSTIONS.includes(imagePath[0]);
  }

  public convertBlobToBase64 = (blob: Blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = reject;
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.readAsDataURL(blob);
    });

  public getFileFromBase64(string64: string, fileExtension: string): File {
    const trimmedString = string64.replace('dataimage/jpegbase64', '');
    const imageContent = atob(trimmedString);
    const buffer = new ArrayBuffer(imageContent.length);
    const view = new Uint8Array(buffer);
    for (let n = 0; n < imageContent.length; n++) {
      view[n] = imageContent.charCodeAt(n);
    }
    const type = `image/${fileExtension}`;
    const blob = new Blob([buffer], { type });
    const dateTime = new Date().getTime();
    return new File([blob], `${dateTime}.${fileExtension}`, {
      lastModified: dateTime,
      type,
    });
  }

  async getJPEGPhotoFromPhotoLibrary(maxSize: number): Promise<Photo> {
    let capturedPhoto: Photo;
    try {
      capturedPhoto = await Camera.getPhoto({
        resultType: CameraResultType.Uri,
        source: CameraSource.Photos,
        quality: 50,
      });
    } catch (error) {
      throw new Error(FILE_ERRORS.canceled);
    }
    const base64Data = await this.readAsBase64(capturedPhoto);
    const imageSize = this.calculateImageSize(base64Data);
    if (imageSize > maxSize) {
      throw new Error(FILE_ERRORS.wrongSize.valueOf());
    }
    return capturedPhoto;
  }

  getUserFileFromPhoto(photo: Photo): UserFile {
    const fileName = new Date().getTime() + '.' + photo.format;
    return {
      name: fileName,
      filepath: fileName,
      webviewPath: this.platform.is('hybrid')
        ? Capacitor.convertFileSrc(photo.webPath)
        : photo.webPath,
      extension: photo.format,
      mimeType: 'image/jpeg', // Hardcoded to image/jpeg since camera plugin converts everything to that
      isLoading: true,
    } as UserFile;
  }

  async photoToBlob(photo, filename) {
    const base64Data = await this.readAsBase64(photo);
    const bstr = atob(base64Data);
    let n = bstr.length;
    let u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: 'image/jpeg' });
  }
}
